import React from 'react';

import {browserHistory, WithRouterProps} from 'react-router';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {ModernLinkTabs} from 'app/general/widgets/ModernTabs';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {useLocalStateFilters} from 'app/hooks';
import {
  useSearchCounts,
  useSearchCountsState,
  useSearchResults,
  useSearchResultsState,
} from 'app/search/api';
import {SearchListItem} from 'app/search/components/SearchListItem';
import {SUPPORT_SEARCH_URL} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {
  SearchFetchParamsType,
  SearchLocationParamsType,
} from 'app/search/types';
import type {SearchContentType, SearchSerializedType} from 'sweet-types';

const searchContentTypes: Array<SearchContentType> = [
  'procedure',
  'proceduregroup',
  'policy',
  'quiz',
];

type RouterProps = WithRouterProps<unknown, SearchLocationParamsType>;

export const SearchDashboard = (routerProps: RouterProps) => {
  const {location} = routerProps;
  const activeContentType =
    location.query.ct != null
      ? searchContentTypes.includes(location.query.ct)
        ? location.query.ct
        : 'procedure'
      : 'procedure';

  const getLinkTo = ({ct, q}: {ct?: SearchContentType; q?: string}) => {
    let query = {...location.query};

    if (location.query.ct !== ct) {
      query.page = '1';
    }

    if (ct != null) {
      query.ct = ct;
    }

    if (q != null) {
      query.q = q;
    }

    return {...location, query};
  };

  const params = convertLocationParamsToFetchParams(location.query);

  const contentTypePluralName =
    activeContentType && getContentType(activeContentType).pluralName;

  return (
    <DashboardPage<
      SearchSerializedType,
      SearchLocationParamsType,
      SearchFetchParamsType
    >
      {...routerProps}
      pageTitle={gt('Search Procedures, Policies and Processes')}
      useDataHook={useSearchResults}
      useFiltersHook={useLocalStateFilters<
        SearchFetchParamsType,
        SearchLocationParamsType
      >(convertLocationParamsToFetchParams)}
      filterbar={() => (
        <div className="mt-3 flex-column flex-grow-1">
          <SearchInputField
            containerClassName="flex-grow-1"
            className="form-control-lg"
            initialValue={location.query.q}
            autoFocus={true}
            placeholder={gt('Search %s...', contentTypePluralName)}
            onChange={(q) => browserHistory.push(getLinkTo({q}))}
          />
          <div className="mt-1 small text-right">
            &nbsp;
            <span>
              {location.query.q ? (
                <a href={SUPPORT_SEARCH_URL}>{gt('search help')}</a>
              ) : null}
            </span>
          </div>
          {
            <ModernLinkTabs
              tabs={searchContentTypes.map((slugCt) => {
                return {
                  label: (
                    <SearchTabLabel
                      key={slugCt}
                      ct={slugCt}
                      activeCt={activeContentType}
                      params={params}
                    />
                  ),
                  to: getLinkTo({
                    ct: slugCt,
                  }),
                  content: () => null,
                };
              })}
            />
          }
        </div>
      )}
      breadcrumbProps={{
        searching: location.query.q != null,
      }}
      messages={{
        notFound: ({viewingAll}, NoResultsMessage) => (
          <NoResultsMessage
            icon="fa-search"
            head={
              viewingAll
                ? gt('Search for %(ct)s', {
                    ct: contentTypePluralName,
                  })
                : gt('No %(ct)s found', {
                    ct: contentTypePluralName,
                  })
            }
          />
        ),
      }}
      renderItem={(searchItem: SearchSerializedType) => (
        <SearchListItem key={searchItem.id} searchItem={searchItem} />
      )}
    />
  );
};

export const SearchBreadcrumb = ({searching, ...other}: any) => (
  <BreadcrumbItemStructure
    text={searching ? gt('Search Results') : gt('Search')}
    icon="fa-search"
    {...other}
  />
);

function SearchTabLabel({
  ct,
  activeCt,
  params,
}: {
  ct: SearchContentType;
  activeCt?: SearchContentType;
  params: SearchFetchParamsType;
}): JSX.Element {
  const updatedParams = {...params, type: ct};

  // fetch counts if ct insn't the active one
  useSearchCounts(updatedParams, {skip: activeCt === ct});
  const {currentData: countsData} = useSearchCountsState(updatedParams);
  const {currentData: resultsData} = useSearchResultsState(updatedParams);

  const total = countsData?.count ?? resultsData?.count;

  return (
    <span>
      {capitalizeFirstLetter(getContentType(ct).name)}{' '}
      <span className={`ml-2 badge badge-${total ? 'secondary' : 'light'}`}>
        {total}
      </span>
      {total == null ? (
        <span className="ml-2">
          <i className="text-secondary fa fa-circle-notch fa-spin small" />
        </span>
      ) : null}
    </span>
  );
}

export const convertLocationParamsToFetchParams = (
  query: SearchLocationParamsType
): SearchFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
  type: query.ct ?? 'procedure',
  search: query.q,
  exclude_folder_id: query.exclude_folder_id,
  exclude_category_id: query.exclude_category_id,
  exclude_quiz_id: query.exclude_quiz_id,
  ordering: query.ordering,
  object_id: query.object_id,
  can_assign: query.can_assign,
});
